import { useState } from 'react';
import AyudaDeLaIA from './AyudaDeLaIA';
import { useAuthStore } from '../store/authStore';
import { HiX } from 'react-icons/hi';
import { notify } from '../utils/notify';
import { productsAPI } from '../services/api';
import { PRODUCT_IMAGE_FALLBACK } from '../utils/imagePlaceholder';
import { useTranslation } from 'react-i18next';
import { mensajeDeError } from '../utils/apiError';
import { calcularEconomia, dinero } from '../utils/economia';
import { comisionDe } from '../utils/comision';

export default function ProductEditModal({ product, onClose, onSave }) {
    const { user } = useAuthStore();
    const esPro = user?.subscription_tier === 'pro';
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        custom_title: product.custom_title || product.original_title || '',
        custom_description: product.custom_description || '',
        custom_price: parseFloat(product.custom_price || 0).toFixed(2),
        markup_percentage: parseFloat(product.markup_percentage || 30).toFixed(1),
    });
    const [loading, setLoading] = useState(false);

    // El margen se aplica sobre el coste puesto en destino, envío incluido
    // cuando se conoce: sobre el precio del artículo a secas, un 30% no cubre
    // un flete que vale tanto como el artículo.
    const costoProducto = parseFloat(product.cost_price || 0);
    const costoEnvio = product.shipping_cost === null || product.shipping_cost === undefined
        ? null
        : parseFloat(product.shipping_cost);

    // Lo que se queda el destino de cada venta. Sin esta resta, el precio que
    // salía de este formulario prometía un margen que la comisión se comía.
    const comision = comisionDe(product.store?.platform, product.store?.settings);

    const cuentaCon = (extra) =>
        calcularEconomia({ costoProducto, costoEnvio, comision, ...extra });

    const handleMarkupChange = (markup) => {
        setFormData({
            ...formData,
            markup_percentage: markup,
            custom_price: cuentaCon({ margen: markup }).precioVenta.toFixed(2)
        });
    };

    const handlePriceChange = (price) => {
        const base = cuentaCon({}).costoTotal;
        const newMarkup = base > 0 ? (((parseFloat(price) - base) / base) * 100).toFixed(1) : '0.0';
        setFormData({
            ...formData,
            custom_price: price,
            markup_percentage: newMarkup
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            notify.loading(t('imported_products.updating'));
            await productsAPI.updateImported(product.id, formData);
            notify.dismiss();
            notify.success(t('imported_products.update_success'));
            onSave();
            onClose();
        } catch (error) {
            notify.dismiss();
            notify.error(mensajeDeError(error, t('imported_products.update_failed')));
        } finally {
            setLoading(false);
        }
    };

    if (!product) return null;

    const cuenta = cuentaCon({ precioVenta: formData.custom_price });
    // Con destino conocido se enseña lo que queda tras la comisión; sin él, lo
    // de siempre, y el aviso de debajo dice que falta esa resta.
    const gananciaMostrada = cuenta.comisionConocida ? cuenta.gananciaNeta : cuenta.ganancia;
    const margenMostrado = (cuenta.comisionConocida ? cuenta.margenNetoReal : cuenta.margenReal).toFixed(1);

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-slate-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
                {/* Header */}
                <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">{t('product_edit.title')}</h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                    >
                        <HiX className="w-5 h-5 text-slate-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Product Preview */}
                    <div className="flex items-center gap-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4">
                        <div className="w-20 h-20 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                            <img
                                src={product.main_image || PRODUCT_IMAGE_FALLBACK}
                                alt={product.custom_title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-500">{t('product_edit.original_title')}</p>
                            <p className="font-medium text-slate-900 dark:text-white line-clamp-2">
                                {product.original_title}
                            </p>
                        </div>
                    </div>

                    {/* Title */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                            {t('product_edit.custom_title')}
                        </label>
                        <input
                            type="text"
                            value={formData.custom_title}
                            onChange={(e) => setFormData({ ...formData, custom_title: e.target.value })}
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder={t('product_edit.custom_title_ph')}
                            required
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                            {t('product_edit.custom_description')}
                        </label>
                        <textarea
                            value={formData.custom_description}
                            onChange={(e) => setFormData({ ...formData, custom_description: e.target.value })}
                            className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent min-h-[120px]"
                            placeholder={t('product_edit.custom_description_ph')}
                            rows={4}
                        />
                    </div>

                    {/* Pricing */}
                    <div className="bg-slate-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-4">
                        <h3 className="font-semibold text-slate-900 dark:text-white">{t('product_edit.pricing')}</h3>

                        <div className="grid grid-cols-2 gap-4">
                            {/* Cost Price (Read-only) */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">
                                    {costoEnvio !== null ? t('product_edit.landed_cost') : t('product_edit.cost_price')}
                                </label>
                                <input
                                    type="text"
                                    value={dinero(cuentaCon({}).costoTotal)}
                                    className="w-full px-4 py-2 bg-slate-600 border border-slate-500 rounded-lg text-slate-300 cursor-not-allowed"
                                    disabled
                                />
                            </div>

                            {/* Selling Price */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-2">
                                    {t('product_edit.selling_price')}
                                </label>
                                <div className="relative">
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min={cuentaCon({}).costoTotal}
                                        value={formData.custom_price}
                                        onChange={(e) => handlePriceChange(e.target.value)}
                                        className="w-full px-4 py-2 pl-7 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Markup Percentage */}
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                {t('product_edit.markup')}: <span className="text-primary-400">{formData.markup_percentage}%</span>
                            </label>
                            <input
                                type="range"
                                min="0"
                                max="500"
                                step="0.5"
                                value={formData.markup_percentage}
                                onChange={(e) => handleMarkupChange(e.target.value)}
                                className="w-full h-2 bg-slate-200 dark:bg-slate-600 rounded-lg appearance-none cursor-pointer accent-primary-500"
                            />
                            <div className="flex justify-between text-xs text-slate-500 mt-1">
                                <span>0%</span>
                                <span>100%</span>
                                <span>200%</span>
                                <span>300%</span>
                                <span>500%</span>
                            </div>
                        </div>

                        {/* Profit Display. La cifra grande es lo que queda
                            DESPUÉS de la comisión del destino cuando se sabe
                            cuál es: es la única que se cobra de verdad. */}
                        <div className="pt-4 border-t border-slate-200 dark:border-slate-600 grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-slate-500">
                                    {cuenta.gananciaCompleta ? t('product_edit.profit_per_unit') : t('product_search.profit_no_shipping')}
                                </p>
                                <p className={`text-lg font-bold ${gananciaMostrada > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                    {dinero(gananciaMostrada)}
                                </p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-500">{t('product_edit.profit_margin')}</p>
                                <p className="text-lg font-bold text-blue-500">{margenMostrado}%</p>
                            </div>
                        </div>
                        {cuenta.comisionConocida ? (
                            <p className="text-[11px] text-slate-400">
                                {t('product_edit.after_fee', {
                                    amount: dinero(cuenta.comision),
                                    percent: cuenta.comisionPct
                                })}
                            </p>
                        ) : (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                {t('product_edit.fee_unknown')}
                            </p>
                        )}
                        {costoEnvio !== null && (
                            <p className="text-[11px] text-slate-400">
                                {t('imported_products.includes_shipping', { amount: dinero(costoEnvio) })}
                            </p>
                        )}
                    </div>

                    {/* Aqui, y no en otra pantalla: la ayuda de la IA sirve
                        mientras se escribe el anuncio y se pone el precio, que
                        es lo que se esta haciendo en este formulario. */}
                    <AyudaDeLaIA
                        producto={product}
                        esPro={esPro}
                        onUsarDescripcion={(d) =>
                            setFormData((actual) => ({
                                ...actual,
                                custom_title: d.title || actual.custom_title,
                                custom_description: d.description || actual.custom_description
                            }))
                        }
                    />

                    {/* Actions */}
                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary flex-1"
                            disabled={loading}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn-primary flex-1"
                            disabled={loading}
                        >
                            {loading ? t('settings.saving') : t('product_edit.save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
